import { ChevronDownIcon } from '@chakra-ui/icons';
import {
  Button,
  Circle,
  Center,
  Flex,
  FormControl,
  Icon,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Tooltip,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';

import { utils as commonUtils } from '@finley-technologies/common';

import {
  DeliverableMetadataFragmentGQLType,
  DeliverableProgressStatusGQLType,
  DeliverableValueTypeGQLType,
} from 'types/dashboard-core-gqlTypes.generated';

import {
  Icon as FAIcon,
  IconSize,
} from 'packages/common-frontend/components/icons/fontAwesome';
import {
  errorToastConfig,
  errorToastId,
  successToastConfig,
} from 'packages/common-frontend/constants';
import { useUpdateDeliverableStatusMutation } from 'packages/deliverables/requests/deliverables.gql.generated';
import {
  isCompletedDeliverableStatus,
  isDueDateOverdue,
  getStatusLabel,
  getComputedStatus,
  ComputedStatus,
} from 'packages/deliverables/ui/utils';
import { FiCheckCircle, FiAlertCircle } from 'react-icons/fi';

type DeliverableProp = Pick<
  DeliverableMetadataFragmentGQLType,
  'id' | 'status' | 'dueDate' | 'name' | 'expectedValueConfig'
>;

const getAvailableStatusChangeOptions = (
  status: DeliverableProgressStatusGQLType,
  deliverableValueType: DeliverableValueTypeGQLType,
): DeliverableProgressStatusGQLType[] => {
  if (deliverableValueType === 'noSubmission') {
    return _.filter(
      ['submitted', 'waived', 'notSubmitted'],
      (currStatus) => status !== currStatus,
    );
  }
  switch (status) {
    case 'notSubmitted':
      return ['waived'];
    case 'submitted':
      return ['waived', 'notSubmitted'];
    case 'resubmit':
      return ['waived', 'notSubmitted'];
    case 'waived':
      return ['notSubmitted'];
    default:
      throw new Error(`Invalid status: ${status}`);
  }
};

const getDeliverableStatusColorScheme = (
  status: DeliverableProgressStatusGQLType,
) => {
  switch (status) {
    case 'notSubmitted':
      return 'blue';
    case 'submitted':
      return 'green';
    case 'resubmit':
      return 'red';
    case 'waived':
    default:
      return 'gray';
  }
};

const StatusIndicatorCircle = ({
  status,
  isOverdue,
}: {
  status: DeliverableProgressStatusGQLType;
  isOverdue: boolean;
}) => {
  const color =
    isOverdue && !isCompletedDeliverableStatus(status)
      ? 'red.300'
      : `${getDeliverableStatusColorScheme(status)}.300`;
  return (
    <Center>
      <Circle
        size="10px"
        bg={isCompletedDeliverableStatus(status) ? color : 'none'}
        border="2px"
        borderColor={color}
      />
    </Center>
  );
};

export const StatusFAIcon = ({
  computedStatus,
  size,
}: {
  computedStatus: ComputedStatus;
  size?: IconSize;
}) => {
  switch (computedStatus) {
    // case 'approved':
    //   return <FAIcon size={size} icon="circle-check" color="green.300" />;
    case 'submitted':
      return <FAIcon size={size} icon="mailbox-flag-up" color="green.300" />;
    // case 'pendingApproval':
    //   return <FAIcon size={size} icon="ballot-check" color="orange.300" />;
    case 'upcoming':
      return <FAIcon size={size} icon="calendar-clock" color="orange.300" />;
    case 'overdue':
      return <FAIcon size={size} icon="calendar-clock" color="red.300" />;
    case 'waived':
      return <FAIcon size={size} icon="circle-check" color="blue.300" />;
    default:
      return <FAIcon size={size} icon="question" color="red.300" />;
  }
};

/** Status change component */

const UpdateStatusFormSelect = ({
  deliverable,
  onUpdate = () => {},
}: {
  deliverable: DeliverableProp;
  onUpdate?: () => void;
}) => {
  const toast = useToast();
  // useDisclosure exposes methods to open and close modal without creating custom dedicated state
  const { isOpen, onClose, onOpen } = useDisclosure();
  const [
    currentStatus,
    setCurrentStatus,
  ] = useState<DeliverableProgressStatusGQLType>(deliverable.status);
  const [approveUpdateStatus, setApproveUpdateStatus] = useState(false);
  const [
    updateDeliverableStatus,
    { loading },
  ] = useUpdateDeliverableStatusMutation({
    fetchPolicy: 'no-cache',
    onError: () => {
      if (!toast.isActive(errorToastId)) {
        errorToastConfig.id = `${deliverable.id}-${currentStatus}-toast`;
        errorToastConfig.title = `Unable to update ${deliverable.name} status. Please try again.`;
        toast(errorToastConfig);
      }
    },
    onCompleted: ({
      updateDeliverableStatus: updateDeliverableStatusResponse,
    }) => {
      onUpdate();
      const { id, name, status: cStatus } = updateDeliverableStatusResponse;
      successToastConfig.id = `${id}-${cStatus}-toast`;
      successToastConfig.title = `${commonUtils.toSentenceCase(
        cStatus,
      )} ${name}`;
      toast(successToastConfig);
    },
  });

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (approveUpdateStatus) {
      updateDeliverableStatus({
        variables: {
          deliverableId: deliverable.id,
          status: currentStatus,
        },
      });
      setApproveUpdateStatus(false);
    }
  }, [approveUpdateStatus]);

  const onClick = (value: DeliverableProgressStatusGQLType) => {
    if (value !== currentStatus) {
      setCurrentStatus(value);
      if (isCompletedDeliverableStatus(value)) {
        // updating status from notStarted -> completed/waived is automatic on select
        updateDeliverableStatus({
          variables: {
            deliverableId: deliverable.id,
            status: value,
          },
        });
      } else {
        // updating status from completed/waived -> notStarted opens a modal for confirmation
        onOpen();
      }
    }
  };

  const handleClose = () => {
    setCurrentStatus(deliverable.status);
    onClose();
  };

  return (
    <FormControl display="flex" w="20%" flexGrow={1}>
      <Modal isOpen={isOpen} onClose={handleClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Confirm status update</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>
              Are you sure you would like to mark {deliverable.name} as{' '}
              {`"${_.lowerCase(currentStatus)}"`}?
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button onClick={handleClose} variant="ghost">
              Cancel
            </Button>
            <Button
              onClick={() => {
                setApproveUpdateStatus(true);
                onClose();
              }}
              colorscheme="teal"
            >
              Mark {`"${_.lowerCase(currentStatus)}"`}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <Menu matchWidth autoSelect={false}>
        <MenuButton
          as={Button}
          isLoading={loading}
          spinnerPlacement="end"
          fontWeight="normal"
          height="100%"
          onClick={(e) => e.stopPropagation()}
          leftIcon={
            <StatusIndicatorCircle
              status={deliverable.status}
              isOverdue={isDueDateOverdue(deliverable.dueDate)}
            />
          }
          rightIcon={<ChevronDownIcon />}
          size="md"
          variant="ghost"
          _active={{ backgroundColor: 'transparent' }}
          _hover={{ backgroundColor: 'transparent' }}
          _focus={{ backgroundColor: 'transparent', boxShadow: 'transparent' }}
        >
          {getStatusLabel(deliverable.status, deliverable.dueDate)}
        </MenuButton>
        <MenuList>
          {_.map(
            getAvailableStatusChangeOptions(
              currentStatus,
              deliverable.expectedValueConfig.valueType,
            ),
            (status) => {
              return (
                <MenuItem
                  key={`${deliverable.id}-option-${status}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onClick(status);
                  }}
                >
                  <Center paddingRight="5px">
                    <StatusIndicatorCircle
                      status={status}
                      isOverdue={isDueDateOverdue(deliverable.dueDate)}
                    />
                  </Center>
                  <Text>{commonUtils.toSentenceCase(status)}</Text>
                </MenuItem>
              );
            },
          )}
        </MenuList>
      </Menu>
    </FormControl>
  );
};
UpdateStatusFormSelect.defaultProps = {
  onUpdate: () => {},
};

/**
 * Customer facing status data cell element for Deliverable Table
 */
export const StatusText = ({
  computedStatus,
}: {
  computedStatus: ComputedStatus;
}) => {
  return (
    <Flex alignItems="center" flexDirection="row">
      <Center paddingRight="5px">
        <StatusFAIcon computedStatus={computedStatus} />
      </Center>
      <Text>{commonUtils.toSentenceCase(computedStatus)}</Text>
    </Flex>
  );
};

/** Container for status */
export const StatusContainer: React.FC<{
  deliverable: DeliverableProp;
  canUpdate: boolean;
  onUpdate?: () => void;
}> = ({ deliverable, canUpdate, onUpdate }) => {
  return canUpdate ? (
    <UpdateStatusFormSelect
      {...{
        deliverable,
        onUpdate,
      }}
    />
  ) : (
    <StatusText
      computedStatus={getComputedStatus(
        deliverable.status,
        deliverable.dueDate,
      )}
    />
  );
};
StatusContainer.defaultProps = { onUpdate: () => {} };

const getDeliverableDependencyStatusColorScheme = (
  status: DeliverableProgressStatusGQLType,
) => {
  switch (status) {
    case 'submitted':
    case 'waived':
      return 'green';
    case 'notSubmitted':
    case 'resubmit':
      return 'red';
    default:
      return 'gray';
  }
};

const getDeliverableDependencyIcon = (
  status: DeliverableProgressStatusGQLType,
) => {
  switch (status) {
    case 'submitted':
    case 'waived':
      return <FiCheckCircle />;
    case 'notSubmitted':
    case 'resubmit':
      return <FiAlertCircle />;
    default:
      return <FiAlertCircle />;
  }
};

export const StatusIcon = ({
  status,
}: {
  status: DeliverableProgressStatusGQLType;
}) => {
  return (
    <Tooltip label={commonUtils.toSentenceCase(status)}>
      <Icon
        color={getDeliverableDependencyStatusColorScheme(status)}
        fontSize="xl"
      >
        {getDeliverableDependencyIcon(status)}
      </Icon>
    </Tooltip>
  );
};
